/**
 * radarDebug.ts — قياسٌ داخل الموقع لسلاسة الرادار.
 *
 * ── لماذا ─────────────────────────────────────────────────────────────────────
 * شُخّص تقطيعُ الرادار يوم 2026-09-20 من مقاطع فيديو ومن متصفّحٍ خارجي، وكلاهما
 * يُضلّل: الفيديو يخلط تغيّر الصورة بتغيّر الشاشة كلّها، والمتصفّح المدمج يخنق
 * `requestAnimationFrame` حين لا تكون نافذته في المقدّمة فتبدو الحلقة مجمّدة وهي
 * تعمل. فخمسة إصلاحات بُنيت على استنتاجٍ لا على رقم.
 *
 * هذا يقيس الأشياء الثلاثة التي تصنع «السلاسة» فعلاً، على جهاز المستخدم نفسه:
 *   • **نبض الساعة**: كم نبضة في الثانية فعلاً، وأطول فجوة بينها.
 *   • **زمن تهيئة المصدر**: من `setTiles` حتى `isSourceLoaded` — فإن تجاوز مدّة
 *     الإطار ظهرت البلاطة بعد دورها، وهو «التأخّر» الذي يصفه المستخدم.
 *   • **قفزات الشفافية**: تغيّرٌ أكبر من 0.4 بين نبضتين متتاليتين يعني قفزةً لا
 *     ذوباناً — وهو «يظهر ويختفي».
 *
 * يُفعَّل بـ`?radarDebug=1` ولا يُحمّل شيئاً ولا يغيّر سلوكاً بدونه.
 */

export const radarDebugOn = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('radarDebug') === '1';

interface Pending { at: number; prefix: string }

const pending = new Map<string, Pending>();
const loadMs: number[] = [];
const gaps: number[] = [];
const jumps: { layer: string; from: number; to: number; t: number; ctx?: unknown }[] = [];
/*
 * لقطة النبضة الجارية: الإطاران، والخانتان، وما تحمله كل خانة. تُرفق بكل قفزة
 * كي يُعرف **لماذا** قفزت — الأرقام وحدها قالت إن المزج لا يحدث عند حدود الخطوات
 * (2026-09-21) ولم تقل أيّ إطار كان يُمزج إليه وأيّ إطار صار حالياً.
 */
let tickCtx: unknown = null;
export function radarDebugContext(ctx: unknown): void {
    if (radarDebugOn) tickCtx = ctx;
}
const longTasks: number[] = [];
let inflight = 0;
let peakInflight = 0;

/*
 * ── من يحجز الخيط الرئيسي، بالاسم ────────────────────────────────────────────
 * قِيس في 2026-09-20 أن **مهمّة واحدة استغرقت 5.7 ثانية**، ومجموع الحجز 47.8 ثانية
 * في قياسٍ مدّته 27.4. ولا شيء في الرادار يفسّر ذلك: البلاطة 244 بايتاً. فالمصدر
 * إذن خارج هذه الطبقة، و`longtask` وحده لا يسمّيه — يقول «مهمّة طويلة» ويصمت.
 *
 * ‏`long-animation-frame` يسمّيه: يعطي لكل سكربت مدّته ومصدره والدالّة التي استدعته.
 * فبدل الاستنتاج، يصير الجواب سطراً في التقرير.
 */
const scripts = new Map<string, { ms: number; n: number }>();

if (radarDebugOn && typeof PerformanceObserver !== 'undefined') {
    try {
        new PerformanceObserver((list) => {
            for (const e of list.getEntries() as unknown as {
                scripts?: { duration: number; sourceURL?: string; invoker?: string; name?: string }[];
            }[]) {
                for (const sc of e.scripts ?? []) {
                    const key = `${(sc.sourceURL || sc.name || '?').split('/').pop()} ← ${sc.invoker || '?'}`;
                    const cur = scripts.get(key) ?? { ms: 0, n: 0 };
                    cur.ms += sc.duration; cur.n += 1;
                    scripts.set(key, cur);
                }
            }
        }).observe({ type: 'long-animation-frame', buffered: true });
    } catch { /* المتصفّح لا يدعمه — بقيّة التقرير تعمل */ }
}

// من يحجز الخيط الرئيسي؟ مهمّة أطول من 50 مللي تعني إطاراً ضائعاً على الأقلّ.
if (radarDebugOn && typeof PerformanceObserver !== 'undefined') {
    try {
        new PerformanceObserver((list) => {
            for (const e of list.getEntries()) longTasks.push(Math.round(e.duration));
        }).observe({ entryTypes: ['longtask'] });
    } catch { /* المتصفّح لا يدعم longtask — البقيّة تعمل */ }
}

/** عدّاد طلبات الإحماء الجارية — يكشف انسداد الطابور. */
export function radarDebugInflight(delta: number): void {
    if (!radarDebugOn) return;
    inflight += delta;
    if (inflight > peakInflight) peakInflight = inflight;
}
let ticks = 0;
let lastTick = 0;
let started = 0;

export function radarDebugTick(): void {
    if (!radarDebugOn) return;
    const now = performance.now();
    if (!started) started = now;
    if (lastTick) gaps.push(now - lastTick);
    lastTick = now;
    ticks++;
}

export function radarDebugSetTiles(src: string, prefix: string): void {
    if (!radarDebugOn) return;
    pending.set(src, { at: performance.now(), prefix });
}

export function radarDebugLoaded(src: string): void {
    if (!radarDebugOn) return;
    const p = pending.get(src);
    if (!p) return;
    pending.delete(src);
    loadMs.push(performance.now() - p.at);
}

export function radarDebugOpacity(layer: string, from: number, to: number): void {
    if (!radarDebugOn) return;
    if (Math.abs(to - from) > 0.4) {
        jumps.push({ layer, from: +from.toFixed(2), to: +to.toFixed(2), t: Math.round(performance.now() - started), ctx: tickCtx });
    }
}

function pct(a: number[], p: number): number {
    if (!a.length) return 0;
    const s = [...a].sort((x, y) => x - y);
    return Math.round(s[Math.min(s.length - 1, Math.floor(s.length * p))]);
}

/** يُستدعى من وحدة التحكّم: `__radarReport()`. */
export function radarDebugReport(): unknown {
    // **من أوّل نبضة إلى آخرها**، لا إلى لحظة كتابة الأمر: النبضات لا تُسجَّل إلا
    // أثناء التشغيل، فقسمتها على الزمن منذ البداية كانت تقيس تأخّر المستخدم في
    // كتابة `__radarReport()` لا إيقاع الحلقة (قِيست 0.8 نبضة/ث وهي في الحقيقة أعلى).
    const secs = Math.max(0.001, (lastTick - started) / 1000);
    return {
        'نبضات/ثانية': +(ticks / Math.max(1, secs)).toFixed(1),
        'أطول فجوة نبض (مللي)': Math.round(Math.max(0, ...gaps)),
        'فجوات > 100 مللي': gaps.filter((g) => g > 100).length,
        'تهيئة مصدر: عدد': loadMs.length,
        'تهيئة مصدر: وسيط (مللي)': pct(loadMs, 0.5),
        'تهيئة مصدر: 90% (مللي)': pct(loadMs, 0.9),
        'تهيئة أبطأ من إطار (>450 مللي)': loadMs.filter((m) => m > 450).length,
        'قفزات شفافية': jumps.length,
        'مهامّ طويلة (>50 مللي)': longTasks.length,
        'أطول مهمّة (مللي)': Math.round(Math.max(0, ...longTasks)),
        'مجموع الحجز (مللي)': longTasks.reduce((a, b) => a + b, 0),
        'ذروة طلبات الإحماء المتوازية': peakInflight,
        'أكثر السكربتات حجزاً': [...scripts.entries()]
            .sort((x, y) => y[1].ms - x[1].ms).slice(0, 6)
            .map(([k, v]) => `${k}: ${Math.round(v.ms)} مللي / ${v.n} مرّة`),
        'أمثلة': jumps.slice(0, 6),
        'أمثلة مفصّلة': jumps.slice(0, 12),
        'مدّة القياس (ث)': +secs.toFixed(1),
    };
}

if (radarDebugOn && typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__radarReport = radarDebugReport;
    // eslint-disable-next-line no-console
    console.info('%c[radar] القياس مفعّل — شغّل الحلقة عشر ثوانٍ ثم اكتب __radarReport()',
        'color:#60a5fa;font-weight:bold');
}
